import { useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Checkbox, Divider, Grid, IconButton, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import PatientsIcon from 'icons/PatientsIcon';
import { dateToString, toTitleCase } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import useGlobalStyles from 'styles/globalStyles';
import { PatientsFiltersDto } from 'types/dto/PatientsFiltersDto';
import { useAuth } from 'context/AuthContext';
import { ImageItem } from 'types/types/Types';
import PatientsFilters from '../Filters/PatientsFilters';
import { useGetCases } from 'hooks/useCases';
import { GetCasesDto } from 'types/dto';
import { tableColumnProps } from 'common/constants';
import CustomCellLabel from '../CustomCellLabel/CustomCellLabel';
import CustomCellValue from '../CustomCellValue/CustomCellValue';
import CaseDocIcon from 'icons/CaseDocIcon';
import InsuranceStatus from 'components/popovers/InsuranceStatus/InsuranceStatus';
import ProcedureActionButtons from '../ActionButtons/ProcedureActionButtons';
import { ESearchBy } from 'enum/ESearchBy';
import DeleteCasesButton from 'components/case/DeleteCasesButton/DeleteCasesButton';
import ExportCasesButton from 'components/case/ExportCasesButton/ExportCasesButton';
import CalendarView from 'components/case/CalendarView/CalendarView';
import { ThinCheckboxIcon } from 'icons/ThinCheckboxIcon';
import { ECaseFilter } from 'enum/ECaseFilter';
import PersonAddIcon from 'icons/PersonAddIcon';
import { useNavigate } from 'react-router-dom';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { ESortType } from 'enum/ESortBy';
import { EPermission } from 'enum/EPermission';

const initialValues = {
  filter: ECaseFilter.ALL,
  search_by: ESearchBy.KEYWORD,
  sort_by: ESortType.DESC,
  value: '',
}

interface Props {
  showAddPatientButton?: boolean;
}

export default function AllPatientsTable({
  showAddPatientButton = false,
}: Props) {
  const { user, doctor } = useAuth();
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<ImageItem[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filters, setFilters] = useState<PatientsFiltersDto>(initialValues);
  const navigate = useNavigate();

  const requestParams: GetCasesDto = {
    userId: doctor?.id ?? user.id,
    page,
    pageLimit: size,
    ...filters,
  };


  const { data, isLoading, isRefetching, refetch } = useGetCases(requestParams,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );


  const columns: GridColDef[] = [
    {
      field: 'name',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Patient Name" />
      ),
      renderCell: (params) => {

        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box>

              {params?.row?.media?.length > 0 ? (
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCarouselImages(params.row.media);
                    setCarouselOpen(true);
                  }}
                >
                  <Box
                    component={'img'}
                    src={params?.row?.media[0]?.urls?.href_small}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '5px',
                      objectFit: 'cover'
                    }}
                  />
                </IconButton>
              ) : (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '5px',
                    backgroundColor: colors.topBackground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CaseDocIcon color={colors.lightgray} size={22} />
                </Box>
              )}
            </Box>
            <CustomText
              text={`${params?.row?.patient_last_name}, ${params?.row?.patient_first_name}`}
              fontSize={15}
              fontWeight={500}
              color={colors.black}
            />
          </Box >
        )
      }
    },
    {
      field: 'date',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Date" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={dateToString(new Date(params.row.date_of_procedure))} />
          </Box >
        )
      }
    },
    {
      field: 'gender',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Gender" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={toTitleCase(params.row.gender)} />
          </Box >
        )
      }
    },
    {
      field: 'type',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Type" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.type} />
          </Box >
        )
      }
    },
    {
      field: 'institution',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Institution" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params?.row?.hospital.name} />
          </Box >
        )
      }
    },
    {
      field: 'procedures',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Procedures" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={String((params.row.case_procedures?.length ?? 0) + 1)} />
          </Box >
        )
      }
    },
    {
      field: 'status',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Status" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            {params.row.insurance_status && <InsuranceStatus item={params.row} refetch={refetch} isEditor={isEditor}/>}
          </Box>
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ProcedureActionButtons
              id={params.row.id}
              hasNotes={!!params.row.discussion_notes_count}
              refetch={refetch}
            />
          </Box>
        )
      }
    },
  ];


  const navigateToAddCase = () => {
    navigate('/patients/add');
  }


  useEffect(() => {
    if (data?.data?.procedures_count !== undefined) {
      setRowCount(data?.data?.procedures_count);
    }
  }, [data?.data?.procedures_count]);

  const isEditor = useMemo(() => {
    return ((doctor && doctor?.permission != EPermission.VIEWER) || !doctor)
  }, [user, doctor]);

  return (
    <Box>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Grid
          container
          alignItems="center"
          spacing={2}
          px={2}
          py={2.5}
        >
          {selectedIds.length > 0 ? (
            /* Selected state */
            <Grid
              size={{ xs: 12 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <CustomText
                text={`${selectedIds.length} selected`}
                fontSize={18}
                fontWeight={600}
                color={colors.primary}
              />

              <Box display='flex' gap={1.5}>
                {isEditor && <DeleteCasesButton
                  ids={selectedIds}
                  refetch={refetch}
                />}

                <ExportCasesButton
                  ids={selectedIds}
                />
              </Box>
            </Grid>
          ) : (
            /* Default state */
            <>
              {/* Left: Icon + Title */}
              <Grid
                size={{ xs: 12, sm: 4 }}
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <Box
                  sx={{
                    backgroundColor: colors.topBackground,
                    display: 'inline-flex',
                    height: 40,
                    width: 40,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '8px',
                  }}
                >
                  <PatientsIcon color={colors.primary} />
                </Box>

                <CustomText
                  text="All Patients"
                  fontSize={16}
                  color={colors.primary}
                  fontWeight={600}
                />
              </Grid>

              {/* Right: Actions */}
              <Grid
                size={{ xs: 12, sm: 8 }}
                display="flex"
                justifyContent={isMobile ? 'stretch' : 'flex-end'}
                gap={1}
              >
                {isMobile ? (
                  <>
                    <CalendarView iconOnly />
                    <IconButton
                      onClick={() => setFiltersOpen(prev => !prev)}
                      sx={{
                        flex: 1,
                        height: 40,
                        borderRadius: '10px',
                        border: `1px solid ${colors.primary}`,
                        color: colors.primary,
                        '&:hover': { borderColor: colors.primary },
                      }}
                    >
                      <FilterIcon />
                    </IconButton>
                    {showAddPatientButton && isEditor && (
                      <IconButton
                        key='add-patient'
                        onClick={navigateToAddCase}
                        sx={{
                          flex: 1,
                          height: 40,
                          borderRadius: '10px',
                          backgroundColor: colors.primary,
                          color: colors.white,
                          '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
                        }}
                      >
                        <PersonAddIcon size={15} />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <>
                    <CalendarView />
                    <CustomButton
                      text="Filter"
                      variant="outlined"
                      height={40}
                      icon={<FilterIcon />}
                      onClick={() => setFiltersOpen(prev => !prev)}
                    />
                    {showAddPatientButton && isEditor && (
                      <CustomButton
                        key='add-patient'
                        text='Add Patient'
                        color='primary'
                        icon={<PersonAddIcon size={15} />}
                        height={40}
                        onClick={navigateToAddCase}
                      />
                    )}
                  </>
                )}
              </Grid>
            </>
          )}
        </Grid>

        <Divider />

        <PatientsFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isRefetching}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
          }}
          onReset={() => {
            setFilters(initialValues);
            setPage(0);
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          localeText={{
            noRowsLabel: 'No patients added yet',
          }}
          onRowSelectionModelChange={(selectionModel: GridRowSelectionModel) => {
            const ids: number[] = Array.from(selectionModel.ids).map((id) => Number(id));
            setSelectedIds(ids);
          }}
          rows={data?.data?.procedures ?? []}
          columns={columns}
          loading={isLoading || isRefetching}
          checkboxSelection
          slots={{
            baseCheckbox: (checkboxProps: any) => (
              <Checkbox
                {...checkboxProps}
                icon={<ThinCheckboxIcon />}
                checkedIcon={<CheckBoxIcon />}
                indeterminateIcon={<IndeterminateCheckBoxIcon />}
              />
            ),
          }}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        onClose={() => setCarouselOpen(false)}
      />
    </Box>
  );
}
